import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, TextField, IconButton, Typography, alpha, Avatar, Stack, Chip,
  CircularProgress, Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { PlantillasDialog } from './PlantillasDialog';
import { textoDeEspera } from '../esperaDeGustavo';
import { etiquetaDeTool } from '../etiquetasDeTools';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import ThumbUpRounded from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRounded from '@mui/icons-material/ThumbDownRounded';
import { Button, Snackbar } from '@mui/material';
import { useUndoGustavoActionMutation, useGetGustavoGreetingQuery, useSubmitGustavoFeedbackMutation } from '../../../store/api/gustavoApi';
import { useSkipGustavoOnboardingMutation } from '../../../store/api/usersApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useGustavoChat, type StreamMessage, type ChatAttachment } from '../hooks/useGustavoChat';
import { ImagenDelChat } from './ImagenDelChat';
import { conSeparadoresDeDia, horaCorta } from '../fechasChat';
import { creditsApi, useGetMyCreditsQuery } from '../../../store/api/creditsApi';
import { useGetCustomerByIdQuery } from '../../../store/api/customersApi';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useGustavoBlocked } from '../../../hooks/useReadOnly';
import { PropertyInlineCard } from './PropertyInlineCard';
import { galeriaDeFotosPara } from './PropertyPhotosInline';
import { CustomerInlineCard } from './CustomerInlineCard';
import { ChatInlineCard } from './ChatInlineCard';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { baseApi } from '../../../store/api/baseApi';

// Tools de Gustavo que MODIFICAN data → al ejecutarse, invalidamos el cache RTK Query
// del recurso afectado para que el CRM se refresque solo (sin recargar el navegador).
const WRITE_TOOL_TAGS: Record<string, string[]> = {
  add_property_images: ['Property'], reorder_property_media: ['Property'],
  update_property: ['Property'], update_property_status: ['Property'],
  reserve_property: ['Property'], create_property: ['Property'],
  discard_property_draft: ['Property'], publish_property_external: ['Property'],
  toggle_property_featured: ['Property', 'MarketplaceSlots'],
  create_customer: ['Customer'], update_customer: ['Customer'], close_customer: ['Customer'],
  transfer_customer: ['Customer'], link_customer: ['Customer'],
  create_note: ['Customer'], update_note: ['Customer'],
  create_opportunity: ['Customer'], update_opportunity: ['Customer'], advance_opportunity_stage: ['Customer'],
  create_event: ['CalendarEvent'], update_event: ['CalendarEvent'], complete_event: ['CalendarEvent'],
  schedule_showing_with_customer: ['CalendarEvent', 'Customer'],
  create_reminder: ['CalendarEvent'], update_reminder: ['CalendarEvent'], complete_reminder: ['CalendarEvent'],
  valuation_captacion: ['Captacion', 'Tasacion'],
  // Parte diario: al enviarlo o registrar una presentación, refrescar el estado del
  // parte (así la burbuja del dashboard se oculta apenas se envía, sin recargar).
  enviar_parte_diario: ['DailyIndicator'],
  registrar_presentacion_comercial: ['DailyIndicator'],
};
const GUSTAVO_AVATAR_URL = 'https://imperium.s3.us-east-1.amazonaws.com/team/gustavo-ejecutivo.png';

const DEFAULT_SUGGESTIONS = [
  '¿Qué tengo pendiente hoy?',
  '¿Cuáles son mis clientes más calientes ahora?',
  'Preparame el brief de mi próxima visita',
  '¿Qué clientes tengo abandonados?',
];

/**
 * Devuelve sugerencias iniciales del chat según la ruta actual del usuario.
 * Si está en la ficha de un cliente, propuesto "resumir / matchear / agendar".
 * Si está en la ficha de una propiedad, "a quién le interesa / cambiar estado / ver oportunidades".
 * Si no hay contexto, cae al set default.
 */
/**
 * Emoji-ancla de cada acción rápida, deducido del texto. La tarjeta necesita algo que
 * la fije visualmente: sin ícono eran cajas con una línea de texto y se leían como
 * campos vacíos, no como algo para tocar.
 */
function iconoSugerencia(texto: string): string {
  const t = texto.toLowerCase();
  if (/visita|agend|brief/.test(t))                 return '📅';
  if (/client|abandonad|caliente|seguimiento/.test(t)) return '👥';
  if (/propiedad|apartamento|casa|busca|mostrame/.test(t)) return '🏠';
  if (/mensaje|whatsapp|escrib/.test(t))            return '💬';
  if (/descripci|portal|public/.test(t))            return '✍️';
  if (/pendiente|hoy|record/.test(t))               return '⏰';
  if (/captar|captaci/.test(t))                     return '🎯';
  return '⚡';
}

function getContextualSuggestions(pathname: string, customerName?: string): string[] {
  const customerMatch = /^\/customers\/([a-f0-9]{24})/i.exec(pathname);
  if (customerMatch) {
    const ref = customerName ?? customerMatch[1];
    return [
      `Resumime al cliente ${ref}`,
      `Matcheá propiedades para el cliente ${ref}`,
      `Agendá una visita con el cliente ${ref}`,
      `Recordame hacer seguimiento del cliente ${ref} mañana a las 10`,
    ];
  }

  const propertyMatch = /^\/properties\/([A-Z0-9-]+)/i.exec(pathname);
  if (propertyMatch) {
    return [
      `¿A qué clientes les puede interesar ${propertyMatch[1]}?`,
      `Generá la descripción de ${propertyMatch[1]} para portal`,
      `Dame la ficha de ${propertyMatch[1]}`,
      `Marcá ${propertyMatch[1]} como reservada`,
    ];
  }

  if (pathname.startsWith('/properties')) {
    return [
      'Buscame apartamentos en Pocitos hasta U$S 200k',
      'Mostrame las propiedades en Carrasco activas',
      '¿Qué propiedades tenemos en alquiler bajo U$S 1.200?',
      'Creá una propiedad nueva',
    ];
  }
  if (pathname.startsWith('/customers')) {
    return [
      '¿Cuáles son mis clientes más calientes ahora?',
      'Mostrame mis clientes en stage de oferta',
      '¿Qué clientes tengo abandonados?',
      'Buscá clientes con el tag "inversor"',
    ];
  }
  if (pathname.startsWith('/calendar')) {
    return [
      'Preparame el brief de mi próxima visita',
      '¿Tengo visitas que necesitan seguimiento?',
      'Agendá una reunión mañana a las 11',
      '¿Qué tengo hoy?',
    ];
  }
  if (pathname.startsWith('/chat')) {
    return [
      'Listame los agentes de mi oficina',
      'Mandale un mensaje a [nombre del agente]',
      'Mostrame los grupos del chat',
    ];
  }
  return DEFAULT_SUGGESTIONS;
}

interface InitialMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Por dónde entró/salió. Gustavo tiene UN hilo por agente compartido entre el CRM
   *  y WhatsApp: esto es lo que permite marcar cuáles vinieron del celular. */
  channel?: 'crm' | 'whatsapp';
  createdAt: string;
}

interface Props {
  initialConversationId?: string;
  initialMessages?:       InitialMessage[];
  userName?:              string;
  autoPrompt?:            string;
  onConversationChange?:  (id: string) => void;
  onStreamEnd?:           () => void;
  /** Queda historial más arriba → al scrollear se pide la página siguiente. */
  hayMasHistorial?:       boolean;
  /** Se está trayendo una página vieja: es el loader de arriba de todo. */
  cargandoMasHistorial?:  boolean;
  /** Trae la página anterior y devuelve sus mensajes (ver `useHistorialGustavo`). */
  onCargarMasHistorial?:  () => Promise<InitialMessage[]>;
  /**
   * Cuántos mensajes del hilo quedaron ARRIBA de los que están cargados.
   *
   * Es lo que mantiene estable el índice que viaja en el 👍/👎: ese número apunta a un
   * mensaje del hilo entero (queda guardado en AIFeedback y el monitor lo usa para saber de
   * cuál se quejaron). Con el historial paginado, el primero que tiene el chat ya no es el
   * primero de la conversación, y sin este corrimiento todos los votos apuntarían al
   * mensaje equivocado.
   */
  offsetHistorial?:       number;
}

/** Roles que ven a Gustavo en modo lectura, con cómo nombrarlos. Espejo de `PERFILES_SIN_CHAT` del backend. */
const PERFILES_SIN_CHAT: Record<string, string> = {
  office_admin: 'administradores de oficina',
  notary_sale:  'la escribanía',
  notary_rent:  'la escribanía',
};

export function ChatPanel({
  initialConversationId, initialMessages, autoPrompt, onConversationChange, onStreamEnd,
  hayMasHistorial, cargandoMasHistorial, onCargarMasHistorial, offsetHistorial = 0,
}: Props) {
  const golden = P.brand.golden;
  const [input, setInput] = useState('');
  // Adjuntos pendientes: previews antes de mandar. Cada uno ya tiene su URL S3.
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const apiBase     = (import.meta as any).env?.VITE_API_URL ?? '/api/v2';
  const navigate = useNavigate();
  const location = useLocation();

  const [plantillasAbiertas, setPlantillasAbiertas] = useState(false);
  const [onboardingSkipped, setOnboardingSkipped] = useState(false);
  const [skipOnboarding] = useSkipGustavoOnboardingMutation();

  const { data: credits } = useGetMyCreditsQuery();
  const hasNoCredits = !!(credits && !credits.unlimited && credits.systemEnabled && credits.balance <= 0);
  // Bloqueo de Gustavo por suscripción suspendida o cupo mensual agotado (tenants booster).
  const gustavoBlock = useGustavoBlocked();
  /**
   * MODO LECTURA para el admin de oficina (20/08/2026, pedido del dueño).
   *
   * Gustavo no está validado para ese perfil: sus tools hablan de "mis clientes" y "mi
   * cartera", que sobre una cuenta de admin no son lo que él administra. Hasta validarlo
   * ve los avisos y el historial de su hilo, pero no le escribe. El backend lo corta
   * igual (403 en `/ai/chat`); esto es para que no escriba contra una puerta cerrada.
   */
  // Desde el 01/10/2026 también la escribanía (notary_sale / notary_rent), mismo pedido.
  const rol = useAppSelector((st) => st.auth.user?.role);
  const perfilSinChat = rol ? PERFILES_SIN_CHAT[rol] : undefined;
  const soloLectura = !!perfilSinChat;

  const { data: greetingData, isLoading: isGreetingLoading } = useGetGustavoGreetingQuery(undefined, {
    skip: !!initialConversationId,
  });

  const effectiveMode = onboardingSkipped ? 'normal' : (greetingData?.greetingMode ?? 'normal');

  const [displayedGreeting, setDisplayedGreeting] = useState('');
  const [isTypingGreeting, setIsTypingGreeting] = useState(false);

  useEffect(() => {
    if (!greetingData?.greeting) return;
    setIsTypingGreeting(true);
    setDisplayedGreeting('');
    let i = 0;
    const text = greetingData.greeting;
    const timer = setInterval(() => {
      i++;
      setDisplayedGreeting(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(timer);
        setIsTypingGreeting(false);
      }
    }, 14);
    return () => clearInterval(timer);
  }, [greetingData?.greeting]);

  const handleSkipOnboarding = async () => {
    setOnboardingSkipped(true);
    skipOnboarding().catch(() => {});
  };

  const customerIdFromPath = useMemo(() => {
    const m = /^\/customers\/([a-f0-9]{24})/i.exec(location.pathname);
    return m ? m[1] : undefined;
  }, [location.pathname]);

  const { data: contextCustomer } = useGetCustomerByIdQuery(customerIdFromPath!, {
    skip: !customerIdFromPath,
  });

  const customerName = contextCustomer
    ? `${contextCustomer.firstName} ${contextCustomer.lastName}`.trim()
    : undefined;

  const suggestions = useMemo(
    () => getContextualSuggestions(location.pathname, customerName),
    [location.pathname, customerName],
  );
  const dispatch = useAppDispatch();
  const { messages, isStreaming, conversationId, send, abort, prepend, undoable, clearUndoable } = useGustavoChat(
    initialConversationId,
    initialMessages,
    {
      onNavigate: (to) => {
        if (to.startsWith('/')) navigate(to);
      },
      onCredits: (ev) => {
        // Patch optimista del balance en el cache de RTK Query.
        dispatch(creditsApi.util.updateQueryData('getMyCredits', undefined, (draft) => {
          if (draft) draft.balance = ev.balance;
        }));
      },
      onInsufficientCredits: () => {
        // Forzamos refetch por las dudas (el server dice 402 pero queremos mostrar balance actualizado).
        dispatch(creditsApi.util.invalidateTags(['Credits']));
      },
      onToolUsed: (name) => {
        // Si Gustavo ejecutó una tool que modifica data visible, invalidar su cache
        // → el CRM se refresca solo (ej. fotos de una propiedad) sin recargar el navegador.
        const tags = WRITE_TOOL_TAGS[name];
        if (tags?.length) dispatch(baseApi.util.invalidateTags(tags as any));
      },
    },
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasStreamingRef = useRef(false);
  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    if (!autoPrompt || hasNoCredits) return;
    const t = setTimeout(() => sendRef.current(autoPrompt), 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrompt]);

  // Voz a texto: el transcript parcial va al input; el final queda fijo.
  const inputBaseRef = useRef('');
  // Último texto que produjo el dictado. Sirve para saber si lo que hay en el
  // input lo dictó la voz (y entonces un audio nuevo arranca limpio) o lo tipeó
  // el agente a mano (y entonces lo respetamos como base y le agregamos la voz).
  const voiceTextRef = useRef('');
  const speech = useSpeechRecognition({
    lang: 'es-UY',
    onInterim: (text) => {
      const next = inputBaseRef.current ? `${inputBaseRef.current} ${text}` : text;
      voiceTextRef.current = next;
      setInput(next);
    },
    onFinalTranscript: (text) => {
      const next = inputBaseRef.current ? `${inputBaseRef.current} ${text}` : text;
      voiceTextRef.current = next;
      setInput(next);
    },
  });
  const toggleMic = () => {
    if (speech.isListening) {
      speech.stop();
    } else {
      const cur = input.trim();
      // Si todo lo que hay en el input lo produjo el dictado anterior, cada audio
      // nuevo arranca limpio (no se reescribe lo de antes). Si el agente escribió
      // algo a mano, lo conservamos como base y la voz se le suma.
      const base = cur && cur === voiceTextRef.current.trim() ? '' : cur;
      if (!base) { voiceTextRef.current = ''; setInput(''); }
      inputBaseRef.current = base;
      speech.start();
    }
  };

  useEffect(() => {
    if (conversationId && onConversationChange) onConversationChange(conversationId);
  }, [conversationId, onConversationChange]);

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      dispatch(baseApi.util.invalidateTags(['GustavoConversation']));
      onStreamEnd?.();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, dispatch, onStreamEnd]);

  const didInitialScrollRef = useRef(false);
  /**
   * Dónde estaba parado el scroll ANTES de que entrara una página vieja arriba.
   *
   * Sin esto, cargar historial se siente como perder el lugar: el contenido crece hacia
   * arriba, el navegador conserva el `scrollTop` en píxeles y lo que se estaba leyendo se
   * va para abajo de golpe. Guardando el alto y la posición se vuelve a poner el mismo
   * mensaje bajo el cursor, que es lo que hace WhatsApp.
   */
  const anclaScrollRef = useRef<{ alto: number; top: number } | null>(null);

  // `useLayoutEffect` y no `useEffect`: corrige la posición ANTES de que el navegador pinte.
  // Con `useEffect` el salto se llegaba a ver como un parpadeo en cada página que entraba.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ancla = anclaScrollRef.current;
    if (ancla) {
      anclaScrollRef.current = null;
      // Lo que creció arriba es la diferencia de alto: se la sumamos a donde estaba.
      el.scrollTop = el.scrollHeight - ancla.alto + ancla.top;
      return;
    }
    // Primer scroll al montar: instantáneo. Los siguientes: suaves.
    const behavior: ScrollBehavior = didInitialScrollRef.current ? 'smooth' : 'auto';
    // `left: 0` explícito: omitirlo conserva el scroll horizontal actual, así que un
    // panel que ya quedó corrido seguía corrido en cada respuesta nueva. Por eso el
    // agente lo notaba "después de varias respuestas seguidas" y no en la primera.
    el.scrollTo({ top: el.scrollHeight, left: 0, behavior });
    didInitialScrollRef.current = true;
  }, [messages]);

  /**
   * Llegar arriba pide la página anterior.
   *
   * El umbral es de 140 px y no de 0: se dispara un poco antes del borde para que la página
   * llegue mientras el agente todavía está scrolleando, y no después de que se choque con
   * el final. `useHistorialGustavo` corta los pedidos repetidos, que en un scroll rápido
   * salen de a varios.
   */
  const UMBRAL_ARRIBA = 140;
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onCargarMasHistorial || !hayMasHistorial || cargandoMasHistorial) return;
    if (el.scrollTop > UMBRAL_ARRIBA) return;
    anclaScrollRef.current = { alto: el.scrollHeight, top: el.scrollTop };
    onCargarMasHistorial().then((viejos) => {
      if (viejos.length) prepend(viejos);
      // Sin mensajes nuevos no hay nada que reacomodar, y un ancla que queda puesta se
      // aplicaría en el próximo mensaje que mande el agente — dejándolo fuera de pantalla.
      else anclaScrollRef.current = null;
    }).catch(() => { anclaScrollRef.current = null; });
  }, [onCargarMasHistorial, hayMasHistorial, cargandoMasHistorial, prepend]);

  /**
   * Lo que se dibuja: las burbujas con un separador cada vez que cambia el día.
   *
   * El `indice` que sale de acá es el del HILO ENTERO (no el de la lista local): se le suma
   * el corrimiento del historial para que el 👍/👎 siga apuntando al mismo mensaje aunque
   * arriba falten páginas sin cargar.
   */
  const itemsDelChat = useMemo(() => {
    const visibles = messages.filter((m, i) => !(m.role === 'assistant' && !m.content && i < messages.length - 1));
    let posicion = 0;
    return conSeparadoresDeDia(visibles).map((item) => (
      item.tipo === 'dia' ? item : { ...item, indice: offsetHistorial + posicion++ }
    ));
  }, [messages, offsetHistorial]);

  const handleSend = () => {
    // No enviar mientras todavía hay fotos subiendo: si no, el agente que adjunta
    // varias y aprieta Enviar manda el mensaje con solo las que alcanzaron a subir
    // (carga serial) y el resto se pierden.
    if (isStreaming || hasNoCredits || uploadingCount > 0 || gustavoBlock.blocked || soloLectura) return;
    if (!input.trim() && pendingAttachments.length === 0) return;
    send(input, { attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined });
    setInput('');
    inputBaseRef.current = '';
    voiceTextRef.current = '';
    setPendingAttachments([]);
  };

  const handlePickFiles = () => {
    if (isStreaming || hasNoCredits) return;
    fileInputRef.current?.click();
  };

  const handleFilesChosen = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    setUploadingCount((c) => c + list.length);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await fetch(`${apiBase}/ai/attachments`, {
          method: 'POST',
          credentials: 'include',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          body: fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = json?.data as ChatAttachment | undefined;
        if (data?.url) {
          setPendingAttachments((prev) => [...prev, { ...data, name: f.name }]);
        }
      } catch {/* el upload falló — lo ignoramos silenciosamente, el agente reintenta */}
      finally {
        setUploadingCount((c) => c - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Plantilla del menú "⋯": se pega en el composer (debajo de lo que ya había) y queda
   * como base para el dictado, igual que si el agente la hubiera tipeado.
   */
  const insertarPlantilla = (texto: string) => {
    const base = input.trim() ? `${input.trimEnd()}\n\n${texto}` : texto;
    setInput(base);
    inputBaseRef.current = base;
    voiceTextRef.current = '';
  };

  const removePending = (url: string) => {
    setPendingAttachments((prev) => prev.filter((p) => p.url !== url));
  };

  const handleSuggestion = (text: string) => {
    if (isStreaming || hasNoCredits) return;
    send(text);
  };

  // Con el hilo vacío las sugerencias ya están como tarjetas grandes en el cuerpo; acá
  // irían repetidas. Y mientras escribe o Gustavo responde, molestan.
  const mostrarAccionesRapidas = messages.length > 0 && !input.trim() && !isStreaming
    && !hasNoCredits && !gustavoBlock.blocked && !soloLectura;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      {/* Messages */}
      {/* `minWidth: 0` + `overflowX: hidden`: sin eso la lista se iba de ancho y quedaba
          corrida en horizontal — se cortaba el principio de cada renglón y sobraba un
          vacío a la derecha. Son dos reglas de CSS que se combinan en contra: un hijo
          flex no puede achicarse por debajo de su contenido (min-width: auto), y cuando
          un eje del overflow es `auto` el otro deja de ser `visible` y también scrollea.
          Con una tarjeta de propiedad adentro (260px mínimos) y la ventana angosta,
          alcanzaba para romperlo. (Agente Demo, 03/08/2026 — 739px de ancho) */}
      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', p: 2 }}>
        {/* Historial: la rueda de arriba mientras baja la página anterior, y el sello de
            "ya está todo" cuando no queda nada más — así el agente sabe que llegó al
            principio y no que se quedó cargando. */}
        {cargandoMasHistorial && (
          <Stack alignItems="center" sx={{ pb: 1.5 }}>
            <CircularProgress size={18} sx={{ color: golden }} />
          </Stack>
        )}
        {!cargandoMasHistorial && hayMasHistorial && messages.length > 0 && (
          <Stack alignItems="center" sx={{ pb: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              Subí para ver mensajes anteriores
            </Typography>
          </Stack>
        )}
        {messages.length === 0 && !hasNoCredits ? (
          <Stack spacing={2.5} sx={{ py: 2 }}>
            {/* Saludo de Gustavo — bubble con typewriter */}
            {(isGreetingLoading || displayedGreeting) && (
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Avatar
                  src={GUSTAVO_AVATAR_URL}
                  alt="Gustavo"
                  sx={{
                    width: 32, height: 32,
                    border: `1px solid ${alpha(golden, 0.4)}`,
                    flexShrink: 0,
                  }}
                />
                {displayedGreeting ? (
                  <Box sx={{
                    maxWidth: '85%',
                    bgcolor: alpha('#141414', 0.8),
                    color: '#F0EDE6', // burbuja de Gustavo siempre oscura → texto claro fijo
                    border: `1px solid ${alpha(golden, 0.08)}`,
                    borderRadius: 2,
                    px: 1.75, py: 1.25,
                    '& p': { my: 0.5, fontSize: 13.5, lineHeight: 1.6 },
                    '& p:first-of-type': { mt: 0 },
                    '& p:last-of-type': { mb: 0 },
                    '& strong': { fontWeight: 700 },
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {displayedGreeting}
                    </ReactMarkdown>
                  </Box>
                ) : null}
              </Stack>
            )}

            {/* Dots de typing — debajo del bubble, mientras carga o escribe */}
            {(isGreetingLoading || isTypingGreeting) && (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{ width: 32, flexShrink: 0 }} />
                <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', pl: 0.25 }}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i} sx={{
                      width: 7, height: 7,
                      borderRadius: '50%',
                      bgcolor: alpha(golden, 0.55),
                      animation: 'gustavoTyping 1.4s infinite ease-in-out',
                      animationDelay: `${i * 0.18}s`,
                      '@keyframes gustavoTyping': {
                        '0%, 80%, 100%': { transform: 'scale(0.55)', opacity: 0.3 },
                        '40%': { transform: 'scale(1)', opacity: 1 },
                      },
                    }} />
                  ))}
                </Box>
              </Stack>
            )}

            {/* Sugerencias — solo cuando terminó de escribir */}
            {!isGreetingLoading && !isTypingGreeting && (
              effectiveMode !== 'normal' && greetingData?.suggestedPrompts?.length ? (
                <Stack spacing={1}>
                  {greetingData.suggestedPrompts.map((s, i) => (
                    <Chip
                      key={i}
                      label={s}
                      onClick={() => handleSuggestion(s)}
                      sx={{
                        justifyContent: 'flex-start',
                        bgcolor: effectiveMode === 'impulso' ? alpha('#3b82f6', 0.08) : alpha(golden, 0.08),
                        border: `1px solid ${alpha(effectiveMode === 'impulso' ? '#3b82f6' : golden, 0.2)}`,
                        color: 'text.primary',
                        fontSize: 12.5,
                        height: 'auto',
                        py: 0.75,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: effectiveMode === 'impulso' ? alpha('#3b82f6', 0.15) : alpha(golden, 0.14),
                        },
                        '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                      }}
                    />
                  ))}
                  {effectiveMode === 'onboarding' && (
                    <Typography
                      onClick={handleSkipOnboarding}
                      sx={{
                        fontSize: 12, color: 'text.disabled', cursor: 'pointer',
                        textAlign: 'right', pt: 0.5,
                        '&:hover': { color: 'text.secondary' },
                      }}
                    >
                      Saltar onboarding →
                    </Typography>
                  )}
                </Stack>
              ) : !greetingData?.hasItems ? (
                /* Tarjetas en dos columnas en vez de chips apilados: es lo primero
                   que ve el agente al abrir y son el atajo a lo que Gustavo hace.
                   Como chips finos se leían como texto de relleno y casi nadie los
                   tocaba; con área grande invitan a arrancar por ahí. */
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}>
                  {suggestions.map((s, i) => (
                    <Box
                      key={i}
                      onClick={() => handleSuggestion(s)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSuggestion(s); }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        minHeight: 54, px: 1.5, py: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: alpha(golden, 0.05),
                        border: `1px solid ${alpha(golden, 0.16)}`,
                        color: 'text.primary',
                        transition: 'transform .12s, background-color .12s, border-color .12s',
                        '&:hover': {
                          bgcolor: alpha(golden, 0.11),
                          borderColor: alpha(golden, 0.4),
                          transform: 'translateY(-1px)',
                        },
                        '&:focus-visible': { outline: `2px solid ${alpha(golden, 0.5)}`, outlineOffset: 2 },
                      }}
                    >
                      {/* El ícono le da ancla visual a la tarjeta: sin él eran cajas con
                          texto suelto, desparejas y con pinta de campo deshabilitado. */}
                      <Box sx={{
                        width: 26, height: 26, borderRadius: 1.25, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(golden, 0.12), color: golden, fontSize: 14,
                      }}>
                        {iconoSugerencia(s)}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, lineHeight: 1.35, fontWeight: 500 }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : null
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {itemsDelChat.map((item) => (
              item.tipo === 'dia' ? (
                <SeparadorDeDia key={item.clave} etiqueta={item.etiqueta} />
              ) : (
                <MessageBubble
                  key={item.clave}
                  message={item.mensaje}
                  conversationId={conversationId}
                  messageIndex={item.indice}
                />
              )
            ))}
          </Stack>
        )}

        {undoable && !isStreaming && (
          <UndoAffordance undoable={undoable} onDismiss={clearUndoable} />
        )}
      </Box>

      {/* Input */}
      <Box sx={{
        p: 1.5,
        borderTop: `1px solid ${alpha(golden, 0.12)}`,
        bgcolor: (t) => alpha(t.palette.background.paper, 0.5),
      }}>
        {/* Previews de adjuntos pendientes — chips con miniatura + x */}
        {(pendingAttachments.length > 0 || uploadingCount > 0) && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {pendingAttachments.map((att) => (
              <Box
                key={att.url}
                sx={{
                  position: 'relative',
                  width: 56, height: 56,
                  borderRadius: 1.2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(golden, 0.3)}`,
                }}
              >
                <Box component="img" src={att.url} alt={att.name ?? 'adjunto'} sx={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                }} />
                <IconButton
                  size="small"
                  onClick={() => removePending(att.url)}
                  sx={{
                    position: 'absolute', top: 2, right: 2,
                    width: 18, height: 18, p: 0,
                    bgcolor: alpha('#000', 0.65),
                    color: '#fff',
                    '&:hover': { bgcolor: '#000' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            ))}
            {Array.from({ length: uploadingCount }).map((_, i) => (
              <Box key={`upl-${i}`} sx={{
                width: 56, height: 56, borderRadius: 1.2,
                bgcolor: alpha(golden, 0.06),
                border: `1px dashed ${alpha(golden, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CircularProgress size={18} sx={{ color: golden }} />
              </Box>
            ))}
          </Stack>
        )}

        {/* File input invisible */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFilesChosen(e.target.files)}
        />

        {soloLectura && (
          <Box sx={{
            mb: 1, px: 1.5, py: 1, borderRadius: 1.5,
            bgcolor: alpha(golden, 0.08), border: `1px solid ${alpha(golden, 0.25)}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.35 }}>
              Gustavo todavía no está habilitado para {perfilSinChat}. Podés ver
              sus avisos y novedades; escribirle va a estar disponible más adelante.
            </Typography>
          </Box>
        )}

        {gustavoBlock.blocked && (
          <Box sx={{
            mb: 1, px: 1.5, py: 1, borderRadius: 1.5,
            bgcolor: alpha('#ef4444', 0.12), border: `1px solid ${alpha('#ef4444', 0.35)}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Typography sx={{ fontSize: 16, lineHeight: 1 }}>⚠️</Typography>
            <Typography variant="caption" sx={{ color: '#fca5a5', lineHeight: 1.35 }}>
              {gustavoBlock.reason}
            </Typography>
          </Box>
        )}

        {/* Acciones rápidas (30/10/2026). Antes eran tarjetas del estado vacío, y con el
            hilo único por agente el hilo nunca está vacío: nadie las veía. Van acá, según
            la pantalla donde está parado, y desaparecen apenas empieza a escribir. */}
        {mostrarAccionesRapidas && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mb: 1, overflowX: 'auto', pb: 0.25,
              scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Chip
              size="small"
              icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Plantillas"
              onClick={() => setPlantillasAbiertas(true)}
              sx={chipAccion(golden, true)}
            />
            {suggestions.map((s) => (
              <Chip
                key={s}
                size="small"
                label={`${iconoSugerencia(s)} ${s}`}
                onClick={() => handleSuggestion(s)}
                sx={chipAccion(golden, false)}
              />
            ))}
          </Stack>
        )}

        {plantillasAbiertas && (
          <PlantillasDialog
            onClose={() => setPlantillasAbiertas(false)}
            onInsertar={(texto) => { setPlantillasAbiertas(false); insertarPlantilla(texto); }}
          />
        )}

        <Stack direction="row" spacing={1} alignItems="flex-end">
          {/* El clip a la vista: estaba detrás de un "⋯" junto con las plantillas y nadie
              lo encontraba. Las plantillas pasaron al chip de arriba. */}
          <IconButton
            onClick={handlePickFiles}
            title="Adjuntar imágenes"
            aria-label="Adjuntar imágenes"
            disabled={isStreaming || hasNoCredits || gustavoBlock.blocked || soloLectura}
            sx={{
              bgcolor: alpha(golden, 0.08),
              color: golden,
              border: `1px solid ${alpha(golden, 0.3)}`,
              '&:hover': { bgcolor: alpha(golden, 0.18) },
              '&.Mui-disabled': { bgcolor: alpha(golden, 0.04), color: alpha(golden, 0.3) },
            }}
          >
            <AttachFileIcon />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Preguntale a Gustavo..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={isStreaming || hasNoCredits || gustavoBlock.blocked || soloLectura}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                fontSize: 14,
                '& fieldset': { borderColor: alpha(golden, 0.2) },
                '&:hover fieldset': { borderColor: alpha(golden, 0.4) },
                '&.Mui-focused fieldset': { borderColor: golden },
              },
            }}
          />
          {speech.isSupported && !isStreaming && (
            <IconButton
              onClick={toggleMic}
              title={speech.isListening ? 'Detener grabación' : 'Hablar'}
              sx={{
                bgcolor: speech.isListening ? alpha('#F87171', 0.15) : alpha(golden, 0.08),
                color:   speech.isListening ? '#F87171' : golden,
                border:  `1px solid ${alpha(speech.isListening ? '#F87171' : golden, 0.3)}`,
                '&:hover': { bgcolor: speech.isListening ? alpha('#F87171', 0.25) : alpha(golden, 0.18) },
                animation: speech.isListening ? 'gustavoMicPulse 1.4s infinite ease-in-out' : 'none',
                '@keyframes gustavoMicPulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${alpha('#F87171', 0.6)}` },
                  '50%':      { boxShadow: `0 0 0 6px ${alpha('#F87171', 0)}` },
                },
              }}
            >
              {speech.isListening ? <StopIcon /> : <MicIcon />}
            </IconButton>
          )}
          {isStreaming ? (
            <IconButton
              onClick={abort}
              sx={{
                bgcolor: alpha('#F87171', 0.1),
                color: '#F87171',
                '&:hover': { bgcolor: alpha('#F87171', 0.2) },
              }}
            >
              <CircularProgress size={18} sx={{ color: '#F87171' }} />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={(!input.trim() && pendingAttachments.length === 0) || hasNoCredits || uploadingCount > 0 || gustavoBlock.blocked || soloLectura}
              sx={{
                bgcolor: (input.trim() || pendingAttachments.length > 0) && !hasNoCredits ? golden : alpha(golden, 0.15),
                color: (input.trim() || pendingAttachments.length > 0) && !hasNoCredits ? '#0A0A0A' : alpha('#0A0A0A', 0.4),
                '&:hover': { bgcolor: P.brand.goldenLight },
                '&.Mui-disabled': { bgcolor: alpha(golden, 0.1), color: alpha(golden, 0.3) },
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Stack>

        {speech.error && (
          <Typography variant="caption" sx={{ color: '#F87171', fontSize: 11, mt: 0.5, display: 'block' }}>
            {speech.error}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, mt: 0.75, display: 'block', textAlign: 'center' }}>
          Gustavo puede cometer errores. Verificá información importante.
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * El separador de día, al estilo WhatsApp: una pastilla centrada entre las burbujas.
 *
 * Va centrado y con fondo propio —y no como texto suelto— porque tiene que leerse como una
 * marca del hilo y no como un mensaje más de Gustavo.
 */
function SeparadorDeDia({ etiqueta }: { etiqueta: string }) {
  const golden = P.brand.golden;
  return (
    <Stack alignItems="center" sx={{ py: 0.5 }}>
      <Box sx={{
        px: 1.25, py: 0.35,
        borderRadius: 999,
        bgcolor: alpha('#141414', 0.75),
        border: `1px solid ${alpha(golden, 0.14)}`,
      }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: alpha('#F0EDE6', 0.75), textTransform: 'uppercase' }}>
          {etiqueta}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * MEMOIZADO a propósito: lo que escribe el agente vive en el estado de ChatPanel, así que
 * cada tecla re-renderiza el panel entero — y con él las burbujas, cada una con su markdown.
 *
 * Marisa lo reportó así: "cuando le escribo a Gustavo el cursor es muy lento, tanto que
 * tengo que esperar para leer lo que escribí… solo me pasa con Gustavo". Su conversación
 * tenía 248 mensajes (122 KB de texto) contra un promedio de 37 en la plataforma: el lag
 * crece con el largo de la charla, por eso "empezó" de un día para el otro y por eso en el
 * resto del CRM escribía normal. (15/08/2026)
 *
 * Las props son estables entre teclas —`messages` no cambia, y los objetos de mensaje
 * conservan identidad porque useGustavoChat actualiza con map/spread—, así que la
 * comparación por defecto de `memo` alcanza: al tipear, las burbujas se saltean enteras.
 * Solo se re-renderiza la que está streameando, que sí cambia de identidad.
 */
const MessageBubble = memo(function MessageBubble({
  message, conversationId, messageIndex,
}: {
  message: StreamMessage;
  conversationId?: string | null;
  messageIndex?:   number;
}) {
  const golden = P.brand.golden;
  if (message.role === 'system' && message.kind === 'no_credits') {
    const isQuota = message.reason === 'quota';
    return (
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Avatar src={GUSTAVO_AVATAR_URL} alt="Gustavo"
          sx={{ width: 32, height: 32, border: `1px solid ${alpha(golden, 0.4)}` }} />
        <Box sx={{
          maxWidth: '85%',
          bgcolor: alpha('#1a0a00', 0.9),
          border: `1px solid ${alpha('#f59e0b', 0.35)}`,
          borderRadius: 2, px: 1.75, py: 1.5,
        }}>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, mb: 1 }}>
            {isQuota ? (
              <>
                Tu organización agotó el <strong>cupo mensual de Gustavo</strong>{message.limit ? <> ({message.limit} créditos)</> : null}.
                {' '}Para seguir usándolo ahora, <strong>subí de plan</strong>; si no, se renueva el próximo período.
              </>
            ) : message.renewsMonthly ? (
              // Régimen por agente (Imperium): la cuota es mensual y se renueva el 1°.
              // Eso es lo primero que tiene que leer — mandarlo a comprar cuando le
              // faltan dos días para la renovación es venderle algo que no necesita.
              <>
                Te quedaste sin créditos para usar Gustavo este mes.{' '}
                <strong>Tenés 0{message.quota ? <> de tu cuota de {message.quota}</> : null}</strong>.
                {' '}Se renuevan <strong>el 1° de cada mes</strong>.
                {' '}Si necesitás seguir antes, conseguí créditos en el Marketplace.
              </>
            ) : (
              // Sin cuota por agente (tenants con pool del plan): no hay 300 ni
              // renovación el 1° que prometer.
              <>
                Te quedaste sin créditos para usar Gustavo. Conseguí más para seguir.
              </>
            )}
          </Typography>
          <RouterLink
            to={isQuota ? '/profile?tab=subscription' : '/marketplace'}
            style={{
              display: 'inline-block',
              background: `linear-gradient(90deg, ${golden}, #f0c96e)`,
              color: '#0A0A0A',
              fontWeight: 700,
              fontSize: 12.5,
              padding: '5px 14px',
              borderRadius: 6,
              textDecoration: 'none',
            }}
          >
            {isQuota ? 'Ver planes →' : 'Conseguir créditos →'}
          </RouterLink>
        </Box>
      </Stack>
    );
  }

  const isUser      = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const msgContent  = message.role !== 'system' ? linkifyPropertyPaths(message.content) : '';
  const hora = message.createdAt instanceof Date ? message.createdAt : new Date(message.createdAt);
  /**
   * La hora no se pinta mientras Gustavo está escribiendo (burbuja con los tres puntitos):
   * ahí todavía no hay mensaje, y la hora de un mensaje que no terminó es la de cuando
   * apareció el placeholder. Tampoco si la fecha no es usable (mensajes viejos sin `createdAt`).
   */
  const mostrarHora = !Number.isNaN(hora.getTime()) && !(isAssistant && !message.content);

  return (
    <Stack direction={isUser ? 'row-reverse' : 'row'} spacing={1.25} alignItems="flex-start">
      {!isUser && (
        <Avatar
          src={GUSTAVO_AVATAR_URL}
          alt="Gustavo"
          sx={{
            width: 32, height: 32,
            border: `1px solid ${alpha(golden, 0.4)}`,
          }}
        />
      )}

      <Box sx={{
        maxWidth: '85%',
        // `minWidth: 0` para que la burbuja PUEDA achicarse: es hija de un flex, y sin
        // esto su ancho mínimo lo fija el contenido más largo — la URL entera — así que
        // el `maxWidth: 85%` no la frenaba y se iba por el costado del panel.
        minWidth: 0,
        bgcolor: isUser ? alpha(golden, 0.12) : alpha('#141414', 0.8),
        // La burbuja de Gustavo (asistente) es SIEMPRE oscura → texto claro fijo (no theme,
        // que en claro lo pondría oscuro e ilegible). La del user adapta al modo.
        color: isUser ? 'text.primary' : '#F0EDE6',
        border: `1px solid ${isUser ? alpha(golden, 0.25) : alpha(golden, 0.08)}`,
        borderRadius: 2,
        px: 1.75, py: 1.25,
      }}>
        {/* Este mensaje pasó por WhatsApp. El hilo es uno solo, así que sin esta marca
            el agente no distingue lo que habló desde el celular de lo del CRM. */}
        {(message as { channel?: string }).channel === 'whatsapp' && (
          <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mb: 0.5, opacity: 0.75 }}>
            <WhatsAppIcon sx={{ fontSize: 12, color: '#25D366' }} />
            <Box component="span" sx={{ fontSize: 9.5, fontWeight: 700, color: '#25D366', letterSpacing: 0.3 }}>
              WhatsApp
            </Box>
          </Stack>
        )}

        {isAssistant && (message as any).toolsUsed?.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mb={0.75}>
            {message.toolsUsed.map((t, i) => (
              <Chip key={i} size="small" label={etiquetaDeTool(t)}
                sx={{
                  height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: alpha(golden, 0.08), color: '#fff',
                  border: `1px solid ${alpha(golden, 0.2)}`,
                }}
              />
            ))}
          </Stack>
        )}

        {/* Miniaturas de adjuntos en el bubble del user (si los hubo) */}
        {isUser && (message as { attachments?: ChatAttachment[] }).attachments?.length ? (
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mb: msgContent ? 0.6 : 0 }}>
            {(message as { attachments: ChatAttachment[] }).attachments.map((att, i) => (
              <Box
                key={`${att.url}-${i}`}
                component="a"
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  width: 80, height: 80,
                  borderRadius: 1.2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(golden, 0.25)}`,
                }}
              >
                <Box component="img" src={att.url} alt={att.name ?? 'adjunto'} sx={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                }} />
              </Box>
            ))}
          </Stack>
        ) : null}

        {isAssistant && !message.content ? (
          <EsperaDeGustavo tool={message.toolsUsed[message.toolsUsed.length - 1]} />
        ) : !msgContent ? null : (
          <Box sx={{
            /**
             * Un link largo se sale de la burbuja.
             *
             * Los avisos de Gustavo traen URLs con el prompt adentro
             * (`/?gustavo=1&prompt=Preparame%20el%20brief%20de%20mi%20pr%C3%B3xima…`) y eso
             * es UNA sola palabra para el navegador: sin dónde cortar, la burbuja se
             * estira y el texto se va por el costado del panel.
             *
             * `anywhere` y no `break-word`: `break-word` solo parte cuando la palabra no
             * entra en una línea vacía, y acá la URL arranca después de texto. `anywhere`
             * corta donde haga falta. Va en el contenedor y no solo en `& a` porque el
             * problema también lo tiene un código pegado o una ruta suelta en el texto.
             * (17/08/2026)
             */
            overflowWrap: 'anywhere',
            minWidth: 0,
            '& p':  { my: 0.5, fontSize: 13.5, lineHeight: 1.6 },
            '& p:first-of-type': { mt: 0 },
            '& p:last-of-type':  { mb: 0 },
            '& ul, & ol': { my: 0.5, pl: 2.5 },
            '& li': { fontSize: 13.5, lineHeight: 1.6, mb: 0.25 },
            '& strong': { fontWeight: 700 },
            '& a': { color: golden, fontWeight: 600, textDecoration: 'none', overflowWrap: 'anywhere', '&:hover': { textDecoration: 'underline' } },
            '& code': {
              bgcolor: alpha(golden, 0.1), color: '#fff',
              px: 0.6, py: 0.1, borderRadius: 0.5, fontSize: 12.5,
              fontFamily: 'ui-monospace, monospace',
            },
            '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 12.5, my: 1 },
            '& th, & td': { border: `1px solid ${alpha(golden, 0.12)}`, px: 1, py: 0.5, textAlign: 'left' },
            '& th': { bgcolor: alpha(golden, 0.05), fontWeight: 700, color: '#fff' },
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Imagen en el mensaje de Gustavo (el aviso del Studio IA manda "cómo quedó"
                // como `![…](url)`): miniatura, y pantalla completa al click.
                img: ({ src, alt }) => <ImagenDelChat src={src} alt={alt} />,
                a: ({ href, children }) => {
                  const internalPath = toInternalPath(href);
                  if (internalPath) {
                    // Si apunta a la bolsa /bolsa-propiedades/<code>, card que lleva a la ficha de la bolsa
                    const bolsaMatch = /^\/bolsa-propiedades\/([A-Z0-9-]+)/i.exec(internalPath);
                    if (bolsaMatch) {
                      return <PropertyInlineCard propertyId={bolsaMatch[1]} to={`/bolsa-propiedades/${bolsaMatch[1]}`} />;
                    }
                    // Galería de fotos descargables (send_property_photos). VA ANTES del
                    // match de propiedad: `/properties/<code>/fotos` también lo matchea y
                    // el agente terminaba viendo la card de la ficha en vez de las fotos.
                    const galeria = galeriaDeFotosPara(internalPath);
                    if (galeria) return galeria;
                    // Si el link apunta a /properties/<code>, reemplazamos por card compacta
                    const propMatch = /^\/properties\/([A-Z0-9-]+)/i.exec(internalPath);
                    if (propMatch) {
                      return <PropertyInlineCard propertyId={propMatch[1]} />;
                    }
                    // Si apunta al chat interno /chat?conv=<id>, preview tapeable de la conversación
                    const chatMatch = /^\/chat\?conv=([a-f0-9]{24})/i.exec(internalPath);
                    if (chatMatch) {
                      return <ChatInlineCard conversationId={chatMatch[1]} />;
                    }
                    // Si apunta a /customers/<id> (sin sub-ruta tipo ?tab=), card de cliente
                    const custMatch = /^\/customers\/([a-f0-9]{24})(?:[/?#]|$)/i.exec(internalPath);
                    if (custMatch && !/[?#]/.test(internalPath)) {
                      return <CustomerInlineCard customerId={custMatch[1]} />;
                    }
                    return <RouterLink to={internalPath} style={{ color: golden, fontWeight: 600 }}>{children}</RouterLink>;
                  }
                  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                },
                // Los `p` de ReactMarkdown son `<p>` por default. Si dentro hay una
                // PropertyInlineCard (que renderiza `<a>` con hijos `<div>`), eso viola
                // validateDOMNesting. Usamos `div` para aceptar cualquier contenido.
                p: ({ children }) => (
                  <Box component="div" sx={{ my: 0.5, fontSize: 13.5, lineHeight: 1.6 }}>
                    {children}
                  </Box>
                ),
              }}
            >
              {msgContent}
            </ReactMarkdown>
          </Box>
        )}
        {/* Lo que Gustavo le mandó por WhatsApp en ese turno (piezas, fotos): en el CRM se ve
            igual, con su link para descargar como las piezas que se piden desde acá. Lo que
            ya viene en el texto (la foto del Studio como ![](url)) no se repite. */}
        {isAssistant && (() => {
          const extra = ((message as { attachments?: ChatAttachment[] }).attachments ?? [])
            .filter((a) => a?.url && !(msgContent ?? '').includes(a.url));
          if (!extra.length) return null;
          return (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 0.5 }}>
              {extra.map((a, i) => (
                <Box key={`${a.url}-${i}`}>
                  <ImagenDelChat src={a.url} alt={`Enviado ${i + 1}`} />
                  <Box component="a" href={a.url} target="_blank" rel="noopener noreferrer" download
                    sx={{ fontSize: 12, color: golden, textDecoration: 'underline' }}>
                    Descargar
                  </Box>
                </Box>
              ))}
            </Stack>
          );
        })()}
        {/* Pie de la burbuja: la hora a la derecha —como WhatsApp— y, en las respuestas de
            Gustavo ya completas, el 👍/👎 a la izquierda. Van en la misma fila para no
            sumarle dos renglones a cada burbuja. */}
        {mostrarHora && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.25 }}>
            {isAssistant && !!message.content && conversationId && messageIndex != null ? (
              <FeedbackThumbs conversationId={conversationId} messageIndex={messageIndex} />
            ) : <Box />}
            <Typography component="span" sx={{
              fontSize: 10,
              lineHeight: 1.6,
              whiteSpace: 'nowrap',
              // Sobre la burbuja oscura de Gustavo el color del tema no se lee: fijo y atenuado.
              color: isUser ? 'text.secondary' : alpha('#F0EDE6', 0.5),
            }}>
              {horaCorta(hora)}
            </Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  );
});

function FeedbackThumbs({ conversationId, messageIndex }: { conversationId: string; messageIndex: number }) {
  const [submit] = useSubmitGustavoFeedbackMutation();
  // Persistir el rating localmente por (conversación, índice de mensaje) para
  // que la selección sobreviva al re-render del bubble (ej. al cambiar de
  // conversación, recargar la página o cuando llega un nuevo mensaje y la
  // lista se rerenderea).
  const storageKey = `gustavo_feedback_${conversationId}_${messageIndex}`;
  const [submitted, setSubmitted] = useState<1 | -1 | null>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === '1' ? 1 : v === '-1' ? -1 : null;
    } catch { return null; }
  });

  const send = (rating: 1 | -1) => {
    if (submitted != null) return;
    setSubmitted(rating);
    try { localStorage.setItem(storageKey, String(rating)); } catch {/* noop */}
    submit({ conversationId, messageIndex, rating }).unwrap().catch(() => {
      setSubmitted(null);
      try { localStorage.removeItem(storageKey); } catch {/* noop */}
    });
  };

  // Una vez votado, no usamos `disabled` (MUI lo pinta gris con .Mui-disabled y
  // tapa el color de la manito elegida). En su lugar bloqueamos el re-voto con
  // pointerEvents y dejamos la seleccionada en su color pleno + la otra atenuada.
  const voted = submitted != null;
  return (
    <Stack direction="row" spacing={0.25} mt={0.75} sx={{ opacity: voted ? 1 : 0.4, transition: 'opacity 0.2s', '&:hover': { opacity: 1 } }}>
      <IconButton
        size="small"
        onClick={() => send(1)}
        aria-label="Respuesta útil"
        sx={{
          p: 0.4,
          color: submitted === 1 ? '#22c55e' : 'rgba(255,255,255,0.45)',
          ...(voted && { pointerEvents: 'none' }),
          ...(voted && submitted !== 1 && { opacity: 0.3 }),
          '&:hover': { color: '#22c55e' },
        }}
      >
        <ThumbUpRounded sx={{ fontSize: 14 }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => send(-1)}
        aria-label="Respuesta no útil"
        sx={{
          p: 0.4,
          color: submitted === -1 ? '#ef4444' : 'rgba(255,255,255,0.45)',
          ...(voted && { pointerEvents: 'none' }),
          ...(voted && submitted !== -1 && { opacity: 0.3 }),
          '&:hover': { color: '#ef4444' },
        }}
      >
        <ThumbDownRounded sx={{ fontSize: 14 }} />
      </IconButton>
    </Stack>
  );
}

/**
 * La burbuja mientras Gustavo trabaja: los puntos y qué está haciendo (según la última
 * tool que arrancó). El aviso de demora ("está tardando más de lo normal") quedó fuera
 * por pedido del user (30/10/2026); `avisoDeDemora` sigue en `esperaDeGustavo.ts`.
 */
function EsperaDeGustavo({ tool }: { tool?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Dot delay={0} />
        <Dot delay={150} />
        <Dot delay={300} />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: alpha('#F0EDE6', 0.7), lineHeight: 1.4 }}>
        {textoDeEspera(tool)}
      </Typography>
    </Stack>
  );
}

/** El chip de acción rápida sobre el composer. El de Plantillas va marcado distinto. */
function chipAccion(golden: string, destacado: boolean) {
  return {
    flexShrink: 0, height: 26, fontSize: 12, cursor: 'pointer',
    bgcolor: alpha(golden, destacado ? 0.14 : 0.06),
    color: 'text.primary',
    border: `1px solid ${alpha(golden, destacado ? 0.45 : 0.2)}`,
    '& .MuiChip-icon': { color: golden },
    '&:hover': { bgcolor: alpha(golden, 0.18), borderColor: alpha(golden, 0.5) },
  } as const;
}

function Dot({ delay }: { delay: number }) {
  const golden = P.brand.golden;
  return (
    <Box sx={{
      width: 6, height: 6, borderRadius: '50%', bgcolor: golden,
      animation: 'gustavoPulse 1.2s infinite ease-in-out',
      animationDelay: `${delay}ms`,
      '@keyframes gustavoPulse': {
        '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
        '40%': { transform: 'scale(1)', opacity: 1 },
      },
    }} />
  );
}

function UndoAffordance({ undoable, onDismiss }: { undoable: { logId: string; expiresAt: number }; onDismiss: () => void }) {
  const golden = P.brand.golden;
  const [remaining, setRemaining] = useState(() => Math.max(0, undoable.expiresAt - Date.now()));
  const [toast, setToast] = useState<{ open: boolean; severity: 'success' | 'error'; text: string }>({ open: false, severity: 'success', text: '' });
  const [undo, { isLoading }] = useUndoGustavoActionMutation();

  useEffect(() => {
    const id = setInterval(() => {
      const left = Math.max(0, undoable.expiresAt - Date.now());
      setRemaining(left);
      if (left === 0) onDismiss();
    }, 1000);
    return () => clearInterval(id);
  }, [undoable.expiresAt, onDismiss]);

  if (remaining === 0) return null;
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  const handleUndo = async () => {
    try {
      await undo(undoable.logId).unwrap();
      setToast({ open: true, severity: 'success', text: 'Acción deshecha' });
      onDismiss();
    } catch (e: any) {
      setToast({ open: true, severity: 'error', text: e?.data?.error ?? 'No se pudo deshacer' });
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} mt={1.5} px={1}>
      <Button
        size="small"
        startIcon={<UndoIcon fontSize="small" />}
        onClick={handleUndo}
        disabled={isLoading}
        sx={{
          bgcolor: alpha(golden, 0.08),
          color: golden,
          border: `1px solid ${alpha(golden, 0.3)}`,
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 700,
          '&:hover': { bgcolor: alpha(golden, 0.15) },
        }}
      >
        Deshacer
      </Button>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
        {mins}:{String(secs).padStart(2, '0')} restante
      </Typography>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ fontSize: 13 }}>{toast.text}</Alert>
      </Snackbar>
    </Stack>
  );
}


/**
 * Devuelve el path interno si el href apunta al CRM (relativo o con dominio). Así
 * recuperamos links aun si el LLM arma un dominio absoluto tipo
 * inmobiliariaimperium.com / boostercrm.app / localhost.
 */
/**
 * Red de seguridad: si Gustavo dejó un path de propiedad SUELTO (sin corchetes),
 * ej. "/properties/IMP-2026-0466", lo envolvemos como link markdown para que el
 * renderer lo muestre como tarjeta (igual que cuando sí lo linkea). Conservador:
 * solo paths relativos al inicio/precedidos por espacio (no toca links ya armados).
 */
function linkifyPropertyPaths(text: string): string {
  return text.replace(
    /(^|[\s>])(\/(?:properties\/[A-Za-z0-9-]+|bolsa-propiedades\/[A-Za-z0-9-]+|chat\?conv=[a-f0-9]{24}|customers\/[a-f0-9]{24}))(?=[\s.,;:)]|$)/gi,
    (_m, pre: string, path: string) => `${pre}[${path}](${path})`,
  );
}

function toInternalPath(href?: string): string | null {
  if (!href) return null;
  if (href.startsWith('/')) return href;
  try {
    const url = new URL(href);
    const host = url.hostname.toLowerCase();
    const isImperiumHost = host === window.location.hostname
      || host.endsWith('inmobiliariaimperium.com')
      || host.endsWith('boostercrm.app')
      || host === 'localhost';
    if (!isImperiumHost) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
